import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  isVisitEditable,
  useArchivedClients,
  useClients,
  useVisitsInRange,
  rangeStartDate,
  type CustomRangeBounds,
  type RangeKey,
  type VisitRow,
} from '@busy/api';
import { RangeSelector } from '../components/dashboard/RangeSelector';
import { EditVisitSheet } from '../components/EditVisitSheet';
import { useT } from '../lib/i18n';
import { useUI } from '../store/ui';

// Cut Log route — opened from the Dashboard's Cuts metric card. Shows
// every visit inside the selected range, newest first, with date/time,
// client name, style (cut_type), length (duration_min), and total $.
// Originally shipped as "Cut List"; renamed to "Cut Log" — "log"
// reads as a record of activity (consistent with the Cut Timer + EOD
// wrap-up language). i18n keys kept under `cutlist.*` to avoid
// churning every reference for a label-only change.
//
// Range comes from a ?range=... URL param so the screen lands aligned
// with whatever range the dashboard was on. The user can re-pick range
// here without bouncing back; the param updates so a back-forward
// gesture preserves their state.

const VALID_RANGES: RangeKey[] = ['today', '7d', '14d', '30d', '90d', 'ytd', 'custom'];

export function CutLog() {
  const t = useT();
  const navigate = useNavigate();
  const language = useUI((s) => s.language);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialRange: RangeKey = (() => {
    const raw = searchParams.get('range') as RangeKey | null;
    // Custom range needs both bounds in the URL; without them, fall back to 7d.
    if (raw === 'custom') {
      const s = searchParams.get('start');
      const e = searchParams.get('end');
      return s && e ? 'custom' : '7d';
    }
    return raw && VALID_RANGES.includes(raw) ? raw : '7d';
  })();
  const initialCustomBounds: CustomRangeBounds | null = (() => {
    if (initialRange !== 'custom') return null;
    const s = searchParams.get('start');
    const e = searchParams.get('end');
    return s && e ? { start: s, end: e } : null;
  })();
  const [range, setRange] = useState<RangeKey>(initialRange);
  const [customBounds, setCustomBounds] = useState<CustomRangeBounds | null>(initialCustomBounds);
  const [editing, setEditing] = useState<VisitRow | null>(null);

  function changeRange(next: RangeKey, custom?: CustomRangeBounds | null) {
    setRange(next);
    setCustomBounds(next === 'custom' ? custom ?? null : null);
    // Sync URL so back/forward preserves range without re-mount. For
    // custom we also persist the bounds so a deep-linked Cut Log URL
    // reproduces the exact view.
    const sp = new URLSearchParams(searchParams);
    sp.set('range', next);
    if (next === 'custom' && custom) {
      sp.set('start', custom.start);
      sp.set('end', custom.end);
    } else {
      sp.delete('start');
      sp.delete('end');
    }
    setSearchParams(sp, { replace: true });
  }

  const visits = useVisitsInRange(range, customBounds);
  const clients = useClients();
  const archived = useArchivedClients();

  // Combined lookup so cuts referencing archived clients still resolve
  // to a name. Falls back to "Walk-in" for null client_id (the schema's
  // anonymous-walk-in shape).
  const clientById = useMemo(() => {
    const m = new Map<string, { first_name: string | null; last_name: string | null }>();
    for (const c of clients.data ?? []) {
      m.set(c.id, { first_name: c.first_name, last_name: c.last_name });
    }
    for (const c of archived.data ?? []) {
      m.set(c.id, { first_name: c.first_name, last_name: c.last_name });
    }
    return m;
  }, [clients.data, archived.data]);

  const list = visits.data ?? [];
  const totalRevenueCents = list.reduce((s, v) => s + (v.price_cents ?? 0), 0);
  const totalTipsCents = list.reduce((s, v) => s + (v.tip_cents ?? 0), 0);

  const dateLocale = language === 'es' ? 'es-US' : 'en-US';
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(dateLocale, {
        month: 'short',
        day: 'numeric',
        weekday: 'short',
      }),
    [dateLocale],
  );
  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(dateLocale, {
        hour: 'numeric',
        minute: '2-digit',
      }),
    [dateLocale],
  );

  return (
    <div className="max-w-page mx-auto p-4 md:p-6 space-y-4 pb-24">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-[12px] text-ink-500 hover:text-ink-700 inline-flex items-center gap-1 mb-1"
          >
            <ChevronLeftIcon className="w-3.5 h-3.5" />
            {t('cutlist.back')}
          </button>
          <h1 className="font-display text-3xl md:text-[36px] text-navy-900 dark:text-gold-500 leading-[1.05]">
            {t('cutlist.title')}
          </h1>
          <p className="text-[14px] text-ink-500 mt-1.5">
            {t(`range.${range}`)} · {formatRangeWindow(range, language, customBounds)}
          </p>
        </div>
        <RangeSelector value={range} customBounds={customBounds} onChange={changeRange} />
      </header>

      {/* Summary chip — count + total $ */}
      <div className="rounded-xl border border-line bg-bg-alt px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
            {t('cutlist.summary.eyebrow')}
          </div>
          <div className="font-display text-2xl text-navy-900 dark:text-gold-500 leading-tight tabular-nums">
            {list.length}{' '}
            <span className="text-[14px] text-ink-700 font-bold tracking-normal">
              {list.length === 1 ? t('cutlist.summary.cut_one') : t('cutlist.summary.cut_other')}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
            {t('cutlist.summary.totalEarned')}
          </div>
          <div className="font-display text-2xl text-navy-900 dark:text-gold-500 leading-tight tabular-nums">
            ${((totalRevenueCents + totalTipsCents) / 100).toFixed(0)}
          </div>
          {totalTipsCents > 0 && (
            <div className="text-[11px] text-green-600 dark:text-green-400 tabular-nums mt-0.5">
              {t('cutlist.summary.includesTips', {
                tips: `$${(totalTipsCents / 100).toFixed(0)}`,
              })}
            </div>
          )}
        </div>
      </div>

      {/* List */}
      {visits.loading && (
        <ul className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <li key={i} className="h-20 rounded-xl bg-bg-alt animate-pulse" />
          ))}
        </ul>
      )}
      {visits.error && (
        <div className="rounded-lg border border-red-100 dark:border-red-600/40 bg-red-50 dark:bg-red-600/15 text-red-700 dark:text-red-300 p-3 text-sm">
          {visits.error}
        </div>
      )}
      {!visits.loading && list.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line bg-bg-alt p-8 text-center">
          <h3 className="font-display text-lg mb-1">{t('cutlist.empty.title')}</h3>
          <p className="text-sm text-ink-500 leading-relaxed max-w-sm mx-auto">
            {t('cutlist.empty.sub')}
          </p>
        </div>
      )}
      {list.length > 0 && (
        <ul className="space-y-2">
          {list.map((v) => {
            const startedDate = new Date(v.started_at);
            const dateStr = dateFmt.format(startedDate);
            const timeStr = timeFmt.format(startedDate);
            const c = v.client_id ? clientById.get(v.client_id) : null;
            const name =
              c == null
                ? t('cutlist.walkIn')
                : `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || t('today.unnamed');
            const style = (v.cut_type ?? '').trim();
            const len = v.duration_min ?? 0;
            const lenLabel = len > 0 ? t('cutlist.minutes', { n: len }) : null;
            const dollars = Math.round((v.price_cents ?? 0) / 100);
            // Per-visit tip + tip rate %. Rate lets the barber compare
            // generosity across price points (a $10 tip on a $30 cut is
            // a bigger gesture than $10 on $100). Empty / zero-tip cuts
            // render nothing so the row stays clean for the majority case.
            const tipCents = v.tip_cents ?? 0;
            const tipRatePct =
              tipCents > 0 && (v.price_cents ?? 0) > 0
                ? Math.round((tipCents / (v.price_cents ?? 0)) * 100)
                : null;
            const editable = isVisitEditable(v);
            // ≥ 6 min = counts toward the earning rate. Surfaces as a
            // small green dot next to the duration so the barber can
            // see which cuts are pulling their $/hr math up.
            const countsTowardRate = len >= 6;
            return (
              <li
                key={v.id}
                className="rounded-xl border border-line bg-surface px-4 py-3 flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[12px] font-semibold text-ink-700 tabular-nums">
                      {dateStr}
                    </span>
                    <span className="text-[12px] text-ink-500 tabular-nums">{timeStr}</span>
                  </div>
                  <strong className="block text-[15px] truncate mt-0.5">{name}</strong>
                  {(style || lenLabel) && (
                    <div className="text-[12px] text-ink-500 mt-0.5 truncate inline-flex items-center gap-1.5">
                      <span>
                        {[style || null, lenLabel].filter(Boolean).join(' · ')}
                      </span>
                      {countsTowardRate && (
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full bg-green-600 shrink-0"
                          title={t('cutlist.rateDotTitle')}
                          aria-label={t('cutlist.rateDotTitle')}
                        />
                      )}
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <div className="text-right">
                    <div className="font-display text-xl text-navy-900 dark:text-gold-500 leading-none tabular-nums">
                      ${dollars}
                    </div>
                    {tipCents > 0 && (
                      <div className="text-[11px] font-bold text-green-600 dark:text-green-400 tabular-nums mt-0.5">
                        {tipRatePct != null
                          ? t('cutlist.tipLineWithRate', {
                              amount: `$${(tipCents / 100).toFixed(
                                tipCents % 100 === 0 ? 0 : 2,
                              )}`,
                              pct: tipRatePct,
                            })
                          : t('cutlist.tipLine', {
                              amount: `$${(tipCents / 100).toFixed(
                                tipCents % 100 === 0 ? 0 : 2,
                              )}`,
                            })}
                      </div>
                    )}
                  </div>
                  {editable && (
                    <button
                      type="button"
                      onClick={() => setEditing(v)}
                      className="w-9 h-9 -mr-1.5 rounded-full grid place-items-center text-ink-500 hover:text-navy-900 hover:bg-bg-alt transition-colors"
                      aria-label={t('cutlist.edit.openAria')}
                      title={t('cutlist.edit.openAria')}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Legend — explains the small green dot next to durations.
          Only renders when there's something in the list to apply to,
          so empty / loading states stay quiet. */}
      {list.length > 0 && (
        <div className="text-[11px] text-ink-500 inline-flex items-center gap-1.5 pt-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-600 shrink-0" aria-hidden />
          <span>{t('cutlist.rateLegend')}</span>
        </div>
      )}

      {editing && (
        <EditVisitSheet visit={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function formatRangeWindow(
  range: RangeKey,
  language: 'en' | 'es',
  custom?: CustomRangeBounds | null,
): string {
  const start = rangeStartDate(range, custom);
  const end = range === 'custom' && custom
    ? new Date(custom.end + 'T00:00:00')
    : (() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
      })();
  const locale = language === 'es' ? 'es-US' : 'en-US';
  const startLabel = start.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  const endLabel = end.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  if (range === 'today') return endLabel;
  if (start.getTime() === end.getTime()) return endLabel;
  return `${startLabel} – ${endLabel}`;
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}
